// Tarjeta del usuario al pie del menú lateral + su menú: Administración y MFA (admins), Mi plan, ayuda
// (consultas sugeridas, preguntas frecuentes, términos, contacto) y Cerrar sesión.
// La de siempre: tarjeta del cliente bajo el logo (sidebarmenu.jsx:938-973; clic de admin → /admin), cerrar sesión
// al pie (1543-1548), MFA en Administración › Usuarios (UserManager.jsx:135) y la ayuda en el menú de perfil que
// quedó sin usar (userprofile.jsx).
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, ChevronsUpDown, FileText, Gauge, HelpCircle, LogOut, Mail, Settings, ShieldCheck } from 'lucide-react';
import { Menu } from '../../ds/components/overlays/Menu';
import { Badge } from '../../ds/components/core/Badge';
import { cn } from '../../ds/lib/cn';
import { useLanguage } from '../../i18n';
import { sidebarCoBrandFor } from '../../config/branding';
import { tieneModulo, useConfigUsuario } from '../app/ConfigUsuario';
import { useNavegacion } from './navegacion';

function Avatar({ src, nombre, className }) {
  const [fallo, setFallo] = useState(false);
  const inicial = (nombre || '?').trim().charAt(0).toUpperCase();
  return (
    <span className={cn('db-relief grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full font-display text-title-sm text-accent-ink', className)}>
      {src && !fallo
        ? <img src={src} alt="" width={40} height={40} className="h-full w-full object-cover" onError={() => setFallo(true)} />
        : <span aria-hidden="true">{inicial}</span>}
    </span>
  );
}

export default function MenuUsuario({ compacto = false, onAbrirMfa }) {
  const { username, esAdmin, organizacion, features, signOut } = useConfigUsuario();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { alNavegar } = useNavegacion();
  const coBrand = sidebarCoBrandFor(username);
  const logo = organizacion.logo || coBrand?.avatar || null;

  const ir = (ruta) => {
    navigate(ruta);
    alNavegar();
  };

  const ayuda = [
    // La guía son consultas sugeridas para el agente: sólo con AI Pricing.
    ...(tieneModulo(features, { feature: 'chatAgent' })
      ? [{ id: 'guia', label: t('menu.guide'), icon: BookOpen, onSelect: () => ir('/guia') }] : []),
    { id: 'faq', label: t('menu.faq'), icon: HelpCircle, onSelect: () => ir('/faq') },
    { id: 'terms', label: t('menu.terms'), icon: FileText, onSelect: () => ir('/terms') },
    { id: 'contact', label: t('menu.contact'), icon: Mail, onSelect: () => ir('/contact') },
  ];
  const items = [
    ...(esAdmin ? [
      { id: 'admin', label: t('menu.administration'), description: t('menu.goToAdmin'), icon: Settings, onSelect: () => ir('/admin') },
      { id: 'mfa', label: t('admin.configureMyMFA'), icon: ShieldCheck, onSelect: () => onAbrirMfa?.() },
    ] : []),
    { id: 'plan', label: t('admin.plan'), icon: Gauge, onSelect: () => ir('/plan') },
    { type: 'section', label: t('menu.help'), items: ayuda },
    { type: 'separator' },
    { id: 'salir', label: t('menu.logout'), icon: LogOut, onSelect: () => signOut() },
  ];

  const nombreCuenta = `Cuenta de ${username || 'usuario'}`;
  return (
    <Menu
      label={nombreCuenta}
      placement="top-start"
      minWidth={256}
      items={items}
      className="w-full"
      trigger={(
        <button
          type="button"
          aria-label={compacto ? nombreCuenta : undefined}
          title={compacto ? nombreCuenta : undefined}
          className={cn(
            'flex min-h-[52px] w-full items-center gap-3 rounded-md p-1.5 text-left transition-colors duration-fast',
            'hover:bg-accent-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            compacto && 'justify-center',
          )}
        >
          <Avatar src={logo} nombre={organizacion.nombre || username} />
          {!compacto && (
            <>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-body-sm font-semibold text-ink">{username}</span>
                {organizacion.nombre && <span className="truncate text-caption text-ink-3">{organizacion.nombre}</span>}
              </span>
              {esAdmin && <Badge tone="accent">ADMIN</Badge>}
              <ChevronsUpDown size={16} className="shrink-0 text-ink-3" aria-hidden="true" />
            </>
          )}
        </button>
      )}
    />
  );
}
